import axios from '../AxiosInstance';
import { getApiUrl } from '../../utils/ApiUtils';

/**
 * Company lookup via the backend TIC proxy (/api/v1/generic/company-lookup).
 *
 * The backend always answers HTTP 200 with an { ok, status, ... } envelope:
 *   ok:true  -> { hits: [NormalizedHit] } / { normalized: NormalizedCompany }
 *   ok:false -> status in ('QUOTA_EXHAUSTED', 'NOT_CONFIGURED',
 *               'UPSTREAM_ERROR', 'QUERY_TOO_SHORT', ...)
 * so callers branch on `status` and fall back to manual entry — never on
 * exceptions. Network/4xx errors are folded into the same envelope here.
 *
 * QUOTA RULE: the TIC plan allows ~200 calls/month platform-wide. Search only
 * on an explicit user action (Sök button / Enter), minimum 3 characters —
 * never per keystroke.
 *
 * A NormalizedHit already carries the complete prefill payload (name, orgnr,
 * address, phone, email, F-skatt/VAT/payroll flags, SNI, derived VAT number),
 * so pickers need NO follow-up details call.
 */

export const searchCompanies = async (query) => {
  try {
    const res = await axios.get(getApiUrl('generic/company-lookup'), {
      params: { query },
      withCredentials: true,
    });
    return res.data;
  } catch (error) {
    if (error.response && error.response.data && error.response.data.status) {
      return error.response.data;
    }
    return { ok: false, status: 'UPSTREAM_ERROR' };
  }
};

export const getCompanyDetails = async (ticCompanyId) => {
  try {
    const res = await axios.get(
      getApiUrl(`generic/company-lookup/${encodeURIComponent(ticCompanyId)}/details`),
      { withCredentials: true }
    );
    return res.data;
  } catch (error) {
    if (error.response && error.response.data && error.response.data.status) {
      return error.response.data;
    }
    return { ok: false, status: 'UPSTREAM_ERROR' };
  }
};

/**
 * Trigger a server-side re-enrichment of the company's stored details from
 * TIC (settings page "Hämta uppgifter" button).
 */
export const enrichCompanyDetails = async (companyId) => {
  try {
    const res = await axios.post(
      getApiUrl(`${companyId}/companydetails/enrich`),
      {},
      { withCredentials: true }
    );
    return res.data;
  } catch (error) {
    if (error.response && error.response.data && error.response.data.status) {
      return error.response.data;
    }
    return { enriched: false, status: 'UPSTREAM_ERROR' };
  }
};

/**
 * Map a lookup failure status to the user-facing Swedish notice, or null
 * when no notice is needed. Shared by all lookup UIs.
 */
export const lookupStatusMessage = (status) => {
  switch (status) {
    case 'QUOTA_EXHAUSTED':
    case 'NOT_CONFIGURED':
      return 'Automatisk hämtning av företagsuppgifter är inte tillgänglig just nu. Fyll i uppgifterna manuellt.';
    case 'UPSTREAM_ERROR':
      return 'Företagsregistret kunde inte nås. Försök igen eller fyll i uppgifterna manuellt.';
    case 'QUERY_TOO_SHORT':
      return 'Skriv minst 3 tecken för att söka.';
    default:
      return null;
  }
};
